import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap, timeout } from 'rxjs/operators';
import { PageRequest } from 'src/app/common/modules/data-table.module/interfaces';
import { ApiResponse } from 'src/app/core/entities/api-response.entity/api-response';
import { ApiService } from 'src/app/core/services/api.service/api.service';
import { DoctorAppointmentBill } from 'src/app/entities/appointment-bill.entity';
import { EAppointmentState } from 'src/app/entities/appointment.entity';
import { DoctorAppointment } from 'src/app/entities/appointment.entity/doctor-appointment.entity/doctor-appointment';
import { DocumentAttachment } from 'src/app/entities/document-attachment.entity/document-attachment';
import { VideoCallRoom } from 'src/app/entities/video-call-room.entity/video-call-room';
import { MILLISECONDS_IN_SECOND } from 'src/app/utils/date.util';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DoctorAppointmentService extends ApiService {

  // ✅ Subject exposé pour que le dashboard (ou tout autre composant) puisse s'abonner
  //    et se rafraîchir dès qu'un rendez-vous change de statut, sans dépendre
  //    d'une navigation ou d'attendre le prochain tick de l'intervalle.
  private refreshPendingCount$ = new Subject<void>();
  readonly refreshPendingCount = this.refreshPendingCount$.asObservable();

  bill(appointemntId: string): Observable<DoctorAppointmentBill> {
    return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/${appointemntId}/invoice`, { responseType: 'json' }).pipe(this.responsePipe);
  }

  all(pageRequest?: PageRequest, query?: { date?: Date, query?: string, dateDebut?: Date, dateFin?: Date }): Observable<Array<DoctorAppointment>> {
    let params = new HttpParams();

    if (pageRequest!.pageOffset) params = params.append('page', pageRequest!.pageOffset)
    if (pageRequest!.pageSize) params = params.append('size', pageRequest!.pageSize);
    if (pageRequest!.sort?.order) {

      let order = "ASC";
      if (pageRequest!.sort.order == "desc") {
        order = "DESC";
        const sortParam = `${pageRequest!.sort.key}`;

        params = params.append("sort", sortParam);
        params = params.append('direction', order)

      } else if (pageRequest!.sort.order == "asc") {
        order = "ASC";
        const sortParam = `${pageRequest!.sort.key}`;

        params = params.append("sort", sortParam);
        params = params.append('direction', order)

      }
    }

    if (query) {
      if (query.date) params = params.append('at', query.date.getTime());
      if (query.dateDebut) params = params.append('after', query.dateDebut.getTime());
      if (query.dateFin) params = params.append('before', query.dateFin.getTime());
      if (query.query) params = params.append('q', query.query);
    }

    if (!query?.query)
      return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/timings`, { params: params, responseType: 'json' }).pipe<Array<DoctorAppointment>>(this.responsePipe);
    return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/timings/search`, { params: params, responseType: 'json' }).pipe<Array<DoctorAppointment>>(this.responsePipe);
  }

  attente(pageRequest?: PageRequest, query?: { date?: Date, query?: string, dateDebut?: Date, dateFin?: Date }): Observable<Array<DoctorAppointment>> {
    let params = new HttpParams();

    if (pageRequest!.pageOffset) params = params.append('page', pageRequest!.pageOffset)
    if (pageRequest!.pageSize) params = params.append('size', pageRequest!.pageSize);
    if (pageRequest!.sort?.order) {

      let order = "ASC";
      if (pageRequest!.sort.order == "desc") {
        order = "DESC";
        const sortParam = `${pageRequest!.sort.key}`;

        params = params.append("sort", sortParam);
        params = params.append('direction', order)

      } else if (pageRequest!.sort.order == "asc") {
        order = "ASC";
        const sortParam = `${pageRequest!.sort.key}`;

        params = params.append("sort", sortParam);
        params = params.append('direction', order)

      }
    }

    if (query) {
      if (query.date) params = params.append('at', query.date.getTime());
      if (query.dateDebut) params = params.append('after', query.dateDebut.getTime());
      if (query.dateFin) params = params.append('before', query.dateFin.getTime());
      if (query.query) params = params.append('q', query.query);
    }

    if (!query?.query)
      return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/timings/onhold`, { params: params, responseType: 'json' }).pipe<Array<DoctorAppointment>>(this.responsePipe);
    return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/timings/search/onhold`, { params: params, responseType: 'json' }).pipe<Array<DoctorAppointment>>(this.responsePipe);
  }

  countPendingAppointments(): Observable<number> {
    return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/timings/onhold/count`, { responseType: 'json' }).pipe<number>(this.responsePipe);
  }

  search(query: { date?: Date, query?: string }): Observable<Array<DoctorAppointment>> {
    let params = new HttpParams();
    if (query.date) params = params.append('date', query.date.getTime());
    if (query.query) params = params.append('query', query.query);

    return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/all/search`, { params: params, responseType: 'json' }).pipe<Array<DoctorAppointment>>(this.responsePipe);
  }

  timings(
    timings: { at?: number; before?: number; after?: number },
    pageRequest: PageRequest,
    query?: { date?: Date; query?: string; assistantId?: string }
  ): Observable<Array<DoctorAppointment>> {

    let params = new HttpParams();

    // =====================
    // TIMINGS
    // =====================
    if (timings?.at != null) params = params.set('at', timings.at);
    if (timings?.before != null) params = params.set('before', timings.before);
    if (timings?.after != null) params = params.set('after', timings.after);

    // =====================
    // PAGINATION
    // =====================
    if (pageRequest?.pageOffset != null) {
      params = params.set('page', pageRequest.pageOffset);
    }

    if (pageRequest?.pageSize != null) {
      params = params.set('size', pageRequest.pageSize);
    }

    // =====================
    // SORT
    // =====================
    if (pageRequest?.sort?.key && pageRequest?.sort?.order) {
      const order = pageRequest.sort.order === 'desc' ? 1 : 0;
      params = params.set('sort', `${pageRequest.sort.key},${order}`);
    }

    // =====================
    // SEARCH MODE
    // =====================
    const hasQuery =
      !!query?.query ||
      !!query?.date ||
      !!query?.assistantId;

    if (hasQuery) {

      if (query?.date) {
        params = params.set('at', query.date.getTime());
      }

      if (query?.query) {
        params = params.set('q', query.query);
      }

      if (query?.assistantId) {
        params = params.set('assistantId', query.assistantId);
      }

      return this.http.get<ApiResponse>(
        `${environment.API_URL}/doctor/appointments/timings/search`,
        { params, responseType: 'json' }
      ).pipe(this.responsePipe);
    }

    // =====================
    // DEFAULT MODE
    // =====================
    return this.http.get<ApiResponse>(
      `${environment.API_URL}/doctor/appointments/timings`,
      { params, responseType: 'json' }
    ).pipe(this.responsePipe);
  }

  add(patientId: string, appointment: DoctorAppointment): Observable<DoctorAppointment> {
    return this.http.post<ApiResponse>(`${environment.API_URL}/doctor/appointments?patientId=${patientId}`, appointment.requestPayload, { responseType: 'json' })
      .pipe(
        this.responsePipe,
        // ✅ Un nouveau RDV peut être créé directement en statut "en attente" → on notifie
        tap(() => this.refreshPendingCount$.next())
      );
  }

  allRdvCalendar(before: number, after: number): Observable<Array<DoctorAppointment>> {
    return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/timings/all?before=${after}&after=${before}`, { responseType: 'json' }).pipe<Array<DoctorAppointment>>(this.responsePipe);
  }

  update(id: string, appointment: DoctorAppointment): Observable<DoctorAppointment> {
    return this.http.put<ApiResponse>(`${environment.API_URL}/doctor/appointments/${id}`, appointment.requestPayload, { responseType: 'json' }).pipe(this.responsePipe)
  }

  getMotifs(idSpecialty: string): Observable<any> {
    return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/consultations/tags/motif/${idSpecialty}`).pipe(this.responsePipe)
  }

  one(appointmentId: string): Observable<DoctorAppointment> {
    return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/${appointmentId}`, { responseType: 'json' }).pipe<DoctorAppointment>(this.responsePipe);
  }

 updateStatus(appointmentId: string, status: EAppointmentState): Observable<DoctorAppointment> {
  return this.http.patch<ApiResponse>(
    `${environment.API_URL}/doctor/appointments/${appointmentId}`,
    {},
    { params: new HttpParams().append('status', status), responseType: 'json' }
  ).pipe<DoctorAppointment>(this.responsePipe)
   .pipe(tap(() => this.refreshPendingCount$.next()));
}

  attachments(appointmentId: string): Observable<Array<DocumentAttachment>> {
    return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/${appointmentId}/attachments`, { responseType: 'json' }).pipe<Array<DocumentAttachment>>(this.responsePipe);
  }

  uploadAttachment(data: FormData, appointmentId: string): Observable<DocumentAttachment> {
    return this.http.post<ApiResponse>(`${environment.API_URL}/doctor/appointments/${appointmentId}/attachments`, data, { responseType: 'json' }).pipe<DocumentAttachment>(this.responsePipe);
  }

  teleconsultation(appointmentId: string): Observable<VideoCallRoom> {
    return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/${appointmentId}/teleconsultation`, { responseType: 'json' }).pipe(timeout(20 * MILLISECONDS_IN_SECOND)).pipe<VideoCallRoom>(this.responsePipe);
  }

  appointmentEnAttente(timings: { before?: number, after?: number }, query?: { date?: Date, query?: string },): Observable<any> {
    let params = new HttpParams();

    if (timings.before != undefined) params = params.append('before', timings.before);
    if (timings.after != undefined) params = params.append('after', timings.after);
    params = params.append('status', 0);

    if (query) {
      if (query.date) params = params.append('at', query.date.getTime());
      if (query.query) params = params.append('q', query.query);
    }
    if (!query)
      return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/timings/all`, { params: params, responseType: 'json' }).pipe<Array<DoctorAppointment>>(this.responsePipe);
    else return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/appointments/timings/all/search`, { params: params, responseType: 'json' }).pipe<Array<DoctorAppointment>>(this.responsePipe);
  }

  getAssistantList(): Observable<Array<any>> {
    return this.http.get<ApiResponse>(`${environment.API_URL}/doctor/medecin-assistants`, { responseType: 'json' }).pipe(this.responsePipe);
  }
}